import React, { useState, useCallback, useRef, useEffect } from 'react';
import {
    Box,
    FormControl,
    FormErrorMessage,
    Input,
    Flex,
    Button,
    Text,
    Spinner,
    Divider,
} from '@chakra-ui/react';
import { usePublishers, Publisher } from '@/features/Quiz/api/getPublishers';
import { useTextbookList, TextbookListItem } from '@/features/Quiz/api/getTextbooks';

type Props = {
    selectedTextbook: TextbookListItem | null;
    onSelect: (textbook: TextbookListItem) => void;
    error?: string;
};

type DropdownPos = { top: number; left: number; width: number };

const PAGE_SIZE = 100;

// ── Shared chevron ────────────────────────────────────────────────────────────

const Chevron: React.FC<{ isOpen: boolean }> = ({ isOpen }) => (
    <Box
        as="span"
        display="inline-block"
        w="0"
        h="0"
        borderLeft="5px solid transparent"
        borderRight="5px solid transparent"
        borderTop={isOpen ? 'none' : '5px solid'}
        borderBottom={isOpen ? '5px solid' : 'none'}
        borderTopColor="gray.500"
        borderBottomColor="gray.500"
        flexShrink={0}
        ml={2}
    />
);

// ── Shared trigger button ─────────────────────────────────────────────────────

type TriggerProps = {
    buttonRef: React.RefObject<HTMLButtonElement>;
    label: string;
    hasValue: boolean;
    isOpen: boolean;
    borderColor: string;
    onClick: () => void;
};

const DropdownTrigger: React.FC<TriggerProps> = ({
    buttonRef,
    label,
    hasValue,
    isOpen,
    borderColor,
    onClick,
}) => (
    // @ts-expect-error Expression produces a union type that is too complex to represent.
    <Button
        ref={buttonRef}
        as="button"
        type="button"
        w="100%"
        h="2.5rem"
        px={3}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        border="1px solid"
        borderColor={borderColor}
        borderRadius="md"
        bg="white"
        fontWeight="normal"
        fontSize="sm"
        _hover={{ bg: 'white' }}
        _active={{ bg: 'white' }}
        onClick={onClick}>
        <Text
            flex="1"
            textAlign="left"
            noOfLines={1}
            color={hasValue ? 'gray.800' : 'gray.400'}
            fontSize="sm">
            {label}
        </Text>
        <Chevron isOpen={isOpen} />
    </Button>
);

// ── Shared dropdown hook ──────────────────────────────────────────────────────

const useDropdown = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [pos, setPos] = useState<DropdownPos | null>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const updatePos = useCallback(() => {
        if (buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            setPos({
                top: rect.bottom + window.scrollY + 4,
                left: rect.left + window.scrollX,
                width: Math.max(rect.width, 220),
            });
        }
    }, []);

    const open = useCallback(
        (onOpen?: () => void) => {
            updatePos();
            setIsOpen(true);
            onOpen?.();
        },
        [updatePos]
    );

    const close = useCallback(() => setIsOpen(false), []);

    const toggle = useCallback(
        (onOpen?: () => void) => {
            if (isOpen) close();
            else open(onOpen);
        },
        [isOpen, open, close]
    );

    useEffect(() => {
        if (!isOpen) return;
        const handler = (e: MouseEvent) => {
            const target = e.target as Node;
            if (
                buttonRef.current &&
                !buttonRef.current.contains(target) &&
                dropdownRef.current &&
                !dropdownRef.current.contains(target)
            ) {
                close();
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen, close]);

    useEffect(() => {
        if (!isOpen) return;
        const handler = () => updatePos();
        window.addEventListener('resize', handler);
        window.addEventListener('scroll', handler, true);
        return () => {
            window.removeEventListener('resize', handler);
            window.removeEventListener('scroll', handler, true);
        };
    }, [isOpen, updatePos]);

    return { isOpen, pos, buttonRef, dropdownRef, toggle, close };
};

// ── Publisher dropdown ────────────────────────────────────────────────────────

type PublisherDropdownProps = {
    value: Publisher | null;
    onChange: (publisher: Publisher | null) => void;
};

const PublisherDropdown: React.FC<PublisherDropdownProps> = ({ value, onChange }) => {
    const { isOpen, pos, buttonRef, dropdownRef, toggle, close } = useDropdown();
    const { data: publishers, isLoading } = usePublishers();

    const handleSelect = useCallback(
        (p: Publisher | null) => {
            onChange(p);
            close();
        },
        [onChange, close]
    );

    const triggerLabel = value ? value.name : 'All publishers';

    return (
        <Box position="relative" w="100%">
            <DropdownTrigger
                buttonRef={buttonRef}
                label={triggerLabel}
                hasValue={value !== null}
                isOpen={isOpen}
                borderColor="gray.200"
                onClick={() => toggle()}
            />

            {isOpen && pos && (
                <Box
                    ref={dropdownRef}
                    position="fixed"
                    zIndex={10000}
                    top={pos.top + 'px'}
                    left={pos.left + 'px'}
                    w={pos.width + 'px'}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    boxShadow="0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -2px rgba(0,0,0,0.05)"
                    maxH="18rem"
                    display="flex"
                    flexDir="column"
                    overflow="hidden">
                    <Box overflowY="auto" flex="1">
                        {isLoading && (
                            <Flex justify="center" py={4}>
                                <Spinner size="sm" />
                            </Flex>
                        )}
                        {/* "All publishers" option */}
                        {!isLoading && (
                            <Box
                                px={3}
                                py="0.625rem"
                                cursor="pointer"
                                bg={value === null ? 'blue.50' : 'white'}
                                _hover={{ bg: value === null ? 'blue.50' : 'gray.50' }}
                                borderBottom="1px solid"
                                borderColor="gray.100"
                                onClick={() => handleSelect(null)}>
                                <Text
                                    fontSize="sm"
                                    fontWeight={value === null ? 'semibold' : 'normal'}
                                    color={value === null ? 'blue.600' : 'gray.500'}>
                                    All publishers
                                </Text>
                            </Box>
                        )}
                        {!isLoading &&
                            publishers?.map((p) => {
                                const isSelected = value?.id === p.id;
                                return (
                                    <Box
                                        key={p.id}
                                        px={3}
                                        py="0.625rem"
                                        cursor="pointer"
                                        bg={isSelected ? 'blue.50' : 'white'}
                                        _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
                                        borderBottom="1px solid"
                                        borderColor="gray.100"
                                        onClick={() => handleSelect(p)}>
                                        <Text
                                            fontSize="sm"
                                            fontWeight={isSelected ? 'semibold' : 'normal'}
                                            color={isSelected ? 'blue.600' : 'gray.800'}>
                                            {p.name}
                                        </Text>
                                    </Box>
                                );
                            })}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

// ── Textbook dropdown ─────────────────────────────────────────────────────────

type TextbookDropdownProps = {
    publisherId: number | null;
    selectedTextbook: TextbookListItem | null;
    onSelect: (textbook: TextbookListItem) => void;
    error?: string;
};

const TextbookDropdown: React.FC<TextbookDropdownProps> = ({
    publisherId,
    selectedTextbook,
    onSelect,
    error,
}) => {
    const { isOpen, pos, buttonRef, dropdownRef, toggle, close } = useDropdown();
    const [search, setSearch] = useState('');
    const searchInputRef = useRef<HTMLInputElement>(null);

    const { data, isLoading } = useTextbookList({
        search,
        publisherId,
        page: 1,
        size: PAGE_SIZE,
    });

    const handleSelect = useCallback(
        (tb: TextbookListItem) => {
            onSelect(tb);
            close();
            setSearch('');
        },
        [onSelect, close]
    );

    const handleToggle = useCallback(() => {
        toggle(() => setTimeout(() => searchInputRef.current?.focus(), 0));
        if (isOpen) setSearch('');
    }, [toggle, isOpen]);

    const editionSuffix = selectedTextbook?.edition
        ? ' (Ed. ' + selectedTextbook.edition + ')'
        : '';
    const triggerLabel = selectedTextbook
        ? selectedTextbook.name + editionSuffix
        : 'Select a textbook...';

    return (
        <Box position="relative" flex="1">
            <FormControl isInvalid={!!error}>
                <DropdownTrigger
                    buttonRef={buttonRef}
                    label={triggerLabel}
                    hasValue={selectedTextbook !== null}
                    isOpen={isOpen}
                    borderColor={error ? 'red.300' : 'gray.200'}
                    onClick={handleToggle}
                />
                {error && <FormErrorMessage>{error}</FormErrorMessage>}
            </FormControl>

            {isOpen && pos && (
                <Box
                    ref={dropdownRef}
                    position="fixed"
                    zIndex={10000}
                    top={pos.top + 'px'}
                    left={pos.left + 'px'}
                    w={pos.width + 'px'}
                    bg="white"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="lg"
                    boxShadow="0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -2px rgba(0,0,0,0.05)"
                    maxH="22rem"
                    display="flex"
                    flexDir="column"
                    overflow="hidden">

                    <Box p={3} pb={0}>
                        <Input
                            ref={searchInputRef}
                            size="sm"
                            placeholder="Search textbook..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            borderRadius="md"
                        />
                    </Box>
                    <Divider mt={3} borderColor="gray.200" />

                    <Box overflowY="auto" flex="1">
                        {isLoading && (
                            <Flex justify="center" py={4}>
                                <Spinner size="sm" />
                            </Flex>
                        )}
                        {!isLoading && data?.results.length === 0 && (
                            <Text px={4} py={6} textAlign="center" color="gray.500" fontSize="sm">
                                No textbooks found.
                            </Text>
                        )}
                        {!isLoading &&
                            data?.results.map((tb) => {
                                const isSelected = selectedTextbook?.domainId === tb.domainId;
                                const itemEdition = tb.edition ? ' (Ed. ' + tb.edition + ')' : '';
                                return (
                                    <Box
                                        key={tb.domainId}
                                        px={3}
                                        py="0.625rem"
                                        cursor="pointer"
                                        bg={isSelected ? 'blue.50' : 'white'}
                                        _hover={{ bg: isSelected ? 'blue.50' : 'gray.50' }}
                                        borderBottom="1px solid"
                                        borderColor="gray.100"
                                        onClick={() => handleSelect(tb)}>
                                        <Text
                                            fontSize="sm"
                                            fontWeight={isSelected ? 'semibold' : 'normal'}
                                            color={isSelected ? 'blue.600' : 'gray.800'}
                                            noOfLines={2}>
                                            {tb.name + itemEdition}
                                        </Text>
                                        {tb.publisher && (
                                            <Text fontSize="xs" color="gray.500" mt="1px">
                                                {tb.publisher.name}
                                                {tb.author ? ' · ' + tb.author : ''}
                                            </Text>
                                        )}
                                    </Box>
                                );
                            })}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

// ── Public component ──────────────────────────────────────────────────────────

export const TextbookPicker: React.FC<Props> = ({ selectedTextbook, onSelect, error }) => {
    const [selectedPublisher, setSelectedPublisher] = useState<Publisher | null>(null);

    return (
        <Box mt={3}>
            <Flex gap={3} align="flex-start">
                <Box w="45%">
                    <Text fontSize="sm" fontWeight="semibold" mb={1}>
                        Publisher
                    </Text>
                    <PublisherDropdown
                        value={selectedPublisher}
                        onChange={setSelectedPublisher}
                    />
                </Box>
                <Box flex="1">
                    <Text fontSize="sm" fontWeight="semibold" mb={1}>
                        Textbook <Box as="span" color="red.500">*</Box>
                    </Text>
                    <TextbookDropdown
                        publisherId={selectedPublisher?.id ?? null}
                        selectedTextbook={selectedTextbook}
                        onSelect={onSelect}
                        error={error}
                    />
                </Box>
            </Flex>
        </Box>
    );
};
